import React, {createContext, useContext, useEffect, useLayoutEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import moment from 'moment';
import {ChevronDownIcon, ChevronUpIcon, CircleX, Search} from 'lucide-react';
import {DOCUMENT_TYPE, TABLE_COLUMNS_TYPES, WORKFLOW_PROCESS_STATUS} from 'utils/constants';
import {TextInput} from 'components/common/TextInput.component';
import {NewCalendar} from 'components/common/calendar/Calendar.component';
import {DropdownList} from 'components/common/dropdown/DropdownList.component';
import {DropdownPosition} from 'types/gaiaComponents/dropdownButton.types';
import Checkbox from 'components/common/checkbox.component';
import {Button, ButtonType} from 'components/common/Button.component';
import {ICON_SIZE, KEY} from 'components/common/constants';
import type {IDropdownListItem} from 'types/gaiaComponents/dropdownButton';
import type {ColumnMapEntry, DataGridFilterContextValue} from 'types/appDataGrid';

// the value a filter is set to when it is cleared, as the backend reads "no filter"
export const NO_FILTER = -1;

// what every filter's leading option reads, and what its column shows while it is picked
const ALL_CAPTION = 'myHistory.all';

// a date filter's value: one ISO day, or the two ends of a range joined by this
const DATE_RANGE_SEPARATOR = '->';
const ISO_DAY = 'YYYY-MM-DD';

/**
 * The filter row's state, handed past ag-grid.
 *
 * ag-grid mounts these widgets itself, so they are not children of the page - but they are
 * rendered inside AgGridReact's own React tree, which a context above it still reaches.
 */
export const DataGridFilterContext = createContext<DataGridFilterContextValue | null>(null);

const parseDateFilterValue = (selectedValue?: string | number) => {
    if (!selectedValue || selectedValue === NO_FILTER) {
        return null;
    }

    const [from, to] = String(selectedValue).split(DATE_RANGE_SEPARATOR);
    const startDate = moment(from, ISO_DAY, true);
    if (!startDate.isValid()) {
        return null;
    }

    const endDate = to ? moment(to, ISO_DAY, true) : startDate;
    return {
        startDate: startDate.toDate(),
        endDate: endDate.isValid() ? endDate.toDate() : startDate.toDate()
    };
};

const isAllSelected = (selectedValue?: string | number) =>
    selectedValue === NO_FILTER || selectedValue === '' || selectedValue == null;

/**
 * A leading "All" option and then one per selector entry. `getValue` is what the option sends
 * the backend and `allValue` what "All" sends, which differs per column type.
 */
function buildFilterItems<TOption>(
    selectedValue: string | number | undefined,
    searchValue: string,
    options: TOption[],
    getValue: (option: TOption) => string | number,
    getText: (option: TOption) => string,
    allValue: string | number,
    context: DataGridFilterContextValue
): IDropdownListItem[] {
    const all = isAllSelected(selectedValue);
    const items: IDropdownListItem[] = [{
        key: 'all',
        text: context.translate(ALL_CAPTION),
        onClick: () => context.onSearch(searchValue, allValue),
        selected: all
    }];

    (options || []).forEach(option => {
        const value = getValue(option);
        items.push({
            key: String(value),
            text: getText(option),
            onClick: () => context.onSearch(searchValue, value),
            selected: !all && selectedValue != null && String(selectedValue) === String(value)
        });
    });

    return items;
}

const TextFilter = ({entry, context}: {entry: ColumnMapEntry; context: DataGridFilterContextValue}) => {
    const value = context.filters.columns?.[entry.searchValue] ?? '';

    // an emoji is not something the backend can search for, so it is dropped rather than sent
    const search = (event: React.ChangeEvent<HTMLInputElement>) => {
        const emoji = /[\p{Extended_Pictographic}\u{1F3FB}-\u{1F3FF}\u{1F9B0}-\u{1F9B3}]+/gu;
        if (!emoji.test(event.target.value)) {
            context.onSearch(entry.searchValue, event.target.value !== '' ? event.target.value : NO_FILTER);
        }
    };

    // TextInput rather than SearchGroupInput: the latter drops the aria-label, and a row of
    // unnamed filter boxes reads as nothing at all
    return (
        <TextInput id={entry.searchValue + '_search'}
                   type="text"
                   className="w-100"
                   value={String(value)}
                   ariaLabel={context.translate(entry.caption)}
                   placeholder={context.translate('myTasks.search')}
                   leftIcon={<Search size={ICON_SIZE.MEDIUM}/>}
                   onChange={search}
                   onClear={() => context.onSearch(entry.searchValue, NO_FILTER)}
                   clearAriaLabel={context.translate('myTasks.clearSearch')}
                   dataCy={'dataGrid.filter.' + entry.searchValue}/>
    );
};

// clear of the trigger, the way the calendar popup sits off its field
const POPUP_MARGIN = 4;

/**
 * A Gaia select whose list is drawn on the body rather than in place.
 *
 * DropdownSelect renders its list inside the cell, and the grid's scrolling viewport clips it -
 * so an open filter list was invisible. The list is portalled out and positioned against the
 * trigger instead, which is how the calendar in the next column over already works.
 *
 * Only the trigger is rebuilt here; the list itself is Gaia's, so the options, their keyboard
 * order and their styling stay the shared ones.
 */
const PortalledSelect = ({id, value, items, ariaLabel}: {
    id: string;
    value: string;
    items: IDropdownListItem[];
    ariaLabel: string;
}) => {
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<{top: number; left: number; width: number} | null>(null);
    const triggerRef = useRef<HTMLDivElement>(null);
    const listRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;

        const closeOnOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (!triggerRef.current?.contains(target) && !listRef.current?.contains(target)) {
                setOpen(false);
            }
        };
        const closeOnEscape = (event: KeyboardEvent) => {
            if (event.key === KEY.ESCAPE) {
                setOpen(false);
            }
        };

        document.addEventListener('mousedown', closeOnOutside);
        document.addEventListener('keydown', closeOnEscape);
        return () => {
            document.removeEventListener('mousedown', closeOnOutside);
            document.removeEventListener('keydown', closeOnEscape);
        };
    }, [open]);

    // before paint, so the list never shows up at the wrong spot first
    useLayoutEffect(() => {
        if (!open) {
            setPosition(null);
            return;
        }

        const place = () => {
            const trigger = triggerRef.current;
            const list = listRef.current;
            if (!trigger) return;

            const rect = trigger.getBoundingClientRect();
            const height = list?.offsetHeight ?? 0;
            // below the trigger, or above it where the page has run out of room
            const below = rect.bottom + POPUP_MARGIN;
            const top = below + height > window.innerHeight && rect.top - POPUP_MARGIN - height >= 0
                ? rect.top - POPUP_MARGIN - height
                : below;
            const left = Math.max(POPUP_MARGIN,
                Math.min(rect.left, window.innerWidth - POPUP_MARGIN - rect.width));

            setPosition({top, left, width: rect.width});
        };

        place();
        window.addEventListener('resize', place);
        // the grid scrolls under the list, and so does the page
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [open]);

    const toggle = () => setOpen(isOpen => !isOpen);

    // a portal's events travel up the React tree, not the DOM one, so a pick inside the list
    // reaches the trigger's own handlers too - and would put the list straight back up
    const fromList = (target: EventTarget | null) => !!listRef.current?.contains(target as Node);

    // the list the combobox owns, named whether or not it is mounted
    const listId = id + '_list';

    // the list's own items close it, so a pick lands and the list goes
    const closingItems = items.map(item => ({
        ...item,
        onClick: () => {
            item.onClick();
            setOpen(false);
        }
    }));

    return (
        <div ref={triggerRef}
             id={id}
             className={`ga-native-select ga-select w-100${open ? ' ga-select--expanded' : ''}`}
             role="combobox"
             aria-haspopup="listbox"
             aria-expanded={open}
             aria-controls={listId}
             aria-label={ariaLabel}
             tabIndex={0}
             onClick={event => {
                 if (!fromList(event.target)) {
                     toggle();
                 }
             }}
             onKeyDown={event => {
                 if (!fromList(event.target) && (event.key === KEY.ENTER || event.key === KEY.SPACE)) {
                     event.preventDefault();
                     toggle();
                 }
             }}>
            <div className="ga-select__main-area">
                <div className="ga-select__value">{value}</div>
            </div>
            <div className="ga-select__suffix">
                {open
                    ? <ChevronUpIcon className="ga-select__action-icon" size={ICON_SIZE.MEDIUM}/>
                    : <ChevronDownIcon className="ga-select__action-icon" size={ICON_SIZE.MEDIUM}/>}
            </div>

            {open && items.length > 0 && createPortal(
                <div ref={listRef}
                     id={listId}
                     className="app-data-grid__filter-popup"
                     style={{
                         position: 'fixed',
                         top: position?.top ?? 0,
                         left: position?.left ?? 0,
                         minWidth: position?.width,
                         visibility: position ? 'visible' : 'hidden'
                     }}>
                    <DropdownList items={closingItems} position={DropdownPosition.BOTTOM_LEFT}/>
                </div>,
                document.body
            )}
        </div>
    );
};

const SelectFilter = ({entry, context}: {entry: ColumnMapEntry; context: DataGridFilterContextValue}) => {
    const selectedValue = context.filters.columns?.[entry.searchValue] ?? '';
    const all = isAllSelected(selectedValue);
    let items: IDropdownListItem[];
    let value: string;

    if (entry.type === TABLE_COLUMNS_TYPES.ROLE_SELECT) {
        const roles = (context.selectors[entry.type] || []) as {actorTypeCode: number; actorTypeName: string}[];
        items = buildFilterItems(selectedValue, entry.searchValue, roles,
            role => role.actorTypeCode, role => role.actorTypeName, '', context);
        const selectedRole = roles.find(role => String(role.actorTypeCode) === String(selectedValue));
        value = all ? context.translate(ALL_CAPTION) : (selectedRole?.actorTypeName ?? '');
    } else if (entry.type === TABLE_COLUMNS_TYPES.STATUS) {
        const states = (context.selectors[entry.type] || []) as {statusCode: number; status: string}[];
        items = buildFilterItems(selectedValue, entry.searchValue, states,
            state => state.statusCode, state => state.status, NO_FILTER, context);
        value = all
            ? context.translate(ALL_CAPTION)
            : context.translate('workflowProcessStatus.' + WORKFLOW_PROCESS_STATUS.asString(Number(selectedValue)));
    } else {
        const documentTypes = (context.selectors['documentType'] || []) as number[];
        items = buildFilterItems(selectedValue, entry.searchValue, documentTypes,
            type => type, type => context.translate('documentType.' + DOCUMENT_TYPE.asString(type)), '', context);
        value = all
            ? context.translate(ALL_CAPTION)
            : context.translate('documentType.' + DOCUMENT_TYPE.asString(Number(selectedValue)));
    }

    return (
        <PortalledSelect id={entry.searchValue + 'Select'}
                         value={value}
                         items={items}
                         ariaLabel={context.translate(entry.caption)}/>
    );
};

const DateFilter = ({entry, context}: {entry: ColumnMapEntry; context: DataGridFilterContextValue}) => {
    const selectedValue = context.filters.columns?.[entry.searchValue];
    const selected = parseDateFilterValue(selectedValue);

    const select = (range: {startDate: Date; endDate: Date}) => {
        const startDate = moment(range.startDate).format(ISO_DAY);
        const endDate = moment(range.endDate).format(ISO_DAY);
        context.onSearch(entry.searchValue,
            startDate !== endDate ? startDate + DATE_RANGE_SEPARATOR + endDate : startDate);
    };

    return (
        <NewCalendar
            // the calendar reads its dates on mount only, so it is remounted when the filter changes under it
            key={String(selectedValue)}
            id={entry.searchValue + '_datepicker'}
            translate={context.translate}
            // a Gaia input is a fixed 200px, which no filter column is; let it take the cell instead
            fieldClassName="w-100"
            {...(selected && {
                prefilled: true,
                initialStartDate: selected.startDate,
                initialEndDate: selected.endDate
            })}
            onChange={range => select(range as {startDate: Date; endDate: Date})}
            onClear={() => context.onSearch(entry.searchValue, NO_FILTER)}/>
    );
};

// the box fills the cell, so the checkbox alone carries the click - a handler on the wrapper
// would run a second time on the click the label forwards to its input
const SelectAllFilter = ({context}: {context: DataGridFilterContextValue}) => (
    <div className="app-data-grid__select-all">
        <Checkbox inputId="headerChk"
                  ariaLabel={context.translate('myTasks.selectAllTasks')}
                  onChange={() => context.selectAll()}
                  isChecked={context.allRowsSelected}/>
    </div>
);

const ClearAllFilter = ({context}: {context: DataGridFilterContextValue}) => {
    const label = context.translate('myHistory.clearAllFilters');

    return (
        <Button type={ButtonType.GHOST}
                iconOnly={true}
                icon={<CircleX size={ICON_SIZE.MEDIUM}/>}
                extraClassName="clear-all-filters-icon"
                disabled={Object.keys(context.filters.columns || {}).length === 0}
                onClick={context.onClearAllFilters}
                ariaLabel={label}
                tooltip={{id: 'clear-all-filters-tooltip', content: () => label}}/>
    );
};

/**
 * The widget under one heading, picked by the column's `type` - the ag-grid floating filter
 * standing in for the legacy table's search row (see TableSearchRow.component).
 *
 * ag-grid hands the column definition's own `column`, so the map entry travels as `entry`.
 */
export const AppDataGridFilter = ({entry}: {entry: ColumnMapEntry}) => {
    const context = useContext(DataGridFilterContext);

    if (!context) {
        return null;
    }

    switch (entry.type) {
        case TABLE_COLUMNS_TYPES.TEXT:
        case TABLE_COLUMNS_TYPES.AMOUNT:
        case TABLE_COLUMNS_TYPES.NUMBER:
            return <TextFilter entry={entry} context={context}/>;
        case TABLE_COLUMNS_TYPES.SELECT:
        case TABLE_COLUMNS_TYPES.ROLE_SELECT:
        case TABLE_COLUMNS_TYPES.STATUS:
            return <SelectFilter entry={entry} context={context}/>;
        case TABLE_COLUMNS_TYPES.DATE_FILTER:
            return <DateFilter entry={entry} context={context}/>;
        case TABLE_COLUMNS_TYPES.CHECKBOX:
            return <SelectAllFilter context={context}/>;
        case TABLE_COLUMNS_TYPES.CLEAR_ALL_FILTERS:
            return <ClearAllFilter context={context}/>;
        // a column drawn as an icon, or one the backend cannot filter by, gets no widget
        case TABLE_COLUMNS_TYPES.NONE:
        case TABLE_COLUMNS_TYPES.TASK_STATUS:
            return null;
        default:
            return <TextFilter entry={entry} context={context}/>;
    }
};

export default AppDataGridFilter;
